// src/views/reports/CabinetsByCityReport.js
import React, {useEffect, useState} from "react";
import {Container, CircularProgress} from "@material-ui/core";
import MUIDataTable from "mui-datatables";
import axios from "axios";
import {getCookie} from "../../components/auth/Helpers";
import {MuiThemeProvider} from "@material-ui/core/styles";
import {datatableTheme} from "assets/css/datatable-theme.js";

const CabinetsByCityReport = () => {
  const token = getCookie("token");
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]); // flattened rows with header rows
  const [rawRows, setRawRows] = useState([]); // backend rows
  const [statusKeys, setStatusKeys] = useState([]);
  const [columns, setColumns] = useState([]);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const resp = await axios.get(`${process.env.REACT_APP_BASE_URL}/reports/cabinets_cities`, {
          headers: {Authorization: `Bearer ${token}`},
        });

        const {rows: dataRows, statusKeys: keys} = resp.data;

        // Build flattened data with header rows per city
        const grouped = [];
        let lastCity = null;
        dataRows.forEach((r) => {
          if (r.city !== lastCity) {
            grouped.push({isHeader: true, city: r.city});
            lastCity = r.city;
          }
          grouped.push({...r, isHeader: false});
        });

        setRawRows(dataRows);
        setRows(grouped);
        setStatusKeys(keys || []);
        setIsLoading(false);
      } catch (err) {
        console.error("fetch report error", err);
        setIsLoading(false);
      }
    };

    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!rows || rows.length === 0) return;

    // Base columns
    const base = [
      {
        name: "city_door",
        label: "City / Door Type",
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: (dataIndex) => {
            const row = rows[dataIndex];
            if (!row) return "";
            if (row.isHeader) return <strong style={{fontSize: 14}}>{row.city}</strong>;
            return (
              <div>
                <div style={{fontWeight: 600}}>{row.door_type} Door</div>
                <div style={{fontSize: 12, color: "#666"}}>{row.type_name || ""}</div>
              </div>
            );
          },
        },
      },
    ];

    // dynamic status columns
    const statusCols = (statusKeys || []).map((status) => ({
      name: status,
      label: status,
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const row = rows[dataIndex];
          if (!row || row.isHeader) return "";
          return row.statusCounts && row.statusCounts[status] ? row.statusCounts[status] : 0;
        },
      },
    }));

    // other counts
    const extras = [
      {
        name: "new_count",
        label: "New",
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: (dataIndex) => {
            const row = rows[dataIndex];
            if (!row || row.isHeader) return "";
            return row.conditionCounts?.New ?? 0;
          },
        },
      },
      {
        name: "used_count",
        label: "Used",
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: (dataIndex) => {
            const row = rows[dataIndex];
            if (!row || row.isHeader) return "";
            return row.conditionCounts?.Used ?? 0;
          },
        },
      },
      {
        name: "is_new_true",
        label: "Is New (true)",
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: (dataIndex) => {
            const row = rows[dataIndex];
            if (!row || row.isHeader) return "";
            return row.isNewCounts?.true ?? 0;
          },
        },
      },
      {
        name: "booked",
        label: "Booked",
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: (dataIndex) => {
            const row = rows[dataIndex];
            if (!row || row.isHeader) return "";
            return row.bookedCounts?.booked ?? 0;
          },
        },
      },
      {
        name: "total",
        label: "Total",
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: (dataIndex) => {
            const row = rows[dataIndex];
            if (!row || row.isHeader) return "";
            return row.total ?? 0;
          },
        },
      },
    ];

    // setColumns([...base, ...statusCols, ...extras]);
    setColumns([
      ...base,
      ...statusCols,
      {
        name: "total",
        label: "Total",
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: (dataIndex) => {
            const row = rows[dataIndex];
            if (!row || row.isHeader) return "";
            return row.total ?? 0;
          },
        },
      },
    ]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, statusKeys]);

  const options = {
    selectableRows: "none",
    filter: false,
    search: false,
    onRowsDelete: null,
    serverSide: false,
    rowsPerPage: 25,
    rowsPerPageOptions: [25, 50, 100],
    responsive: "standard",
    setRowProps: (row, dataIndex) => {
      const r = rows[dataIndex];
      if (r?.isHeader) {
        return {style: {backgroundColor: "#e6f2ff", fontWeight: "700"}};
      }
      return {};
    },
  };

  return (
    <Container maxWidth="xl" style={{marginTop: 20}}>
      {!isLoading ? (
        <MuiThemeProvider theme={datatableTheme}>
          <MUIDataTable data={rows} columns={columns} options={options} />
        </MuiThemeProvider>
      ) : (
        <div style={{display: "flex", justifyContent: "center", marginTop: 50}}>
          <CircularProgress />
        </div>
      )}
    </Container>
  );
};

export default CabinetsByCityReport;
